import { useState } from "react";
import { trpc } from "../lib/trpc";
import { Card, Stat, Badge, Spinner, Input, Select, Button, cx } from "../components/ui";
import { fmtNum, fmtDateTime, todayIso } from "../lib/format";

type StatusFilter =
  | "all" | "mismatches" | "both_active" | "both_inactive"
  | "left_active_exact_inactive" | "left_inactive_exact_active" | "left_only" | "exact_only";

const STATUS_LABEL: Record<string, string> = {
  both_active: "Both Active",
  both_inactive: "Both Inactive",
  mismatch_pbs_active: "Active / ExactOnline Inactive",
  mismatch_ls_active: "Active / ExactOnline Inactive",
  mismatch_exact_active: "Inactive / ExactOnline Active",
  pbs_only: "Only in PBS",
  ls_only: "Only in LS",
  exact_only_active: "Only in ExactOnline",
  exact_only_inactive: "Only in ExactOnline (inactive)",
  exact_on_shop: "On shop (excluded / no SKU)",
};
const statusColor = (s: string) =>
  s === "both_active" ? "green" : s.startsWith("mismatch") ? "amber" : s.endsWith("_only") || s.startsWith("exact_only") || s === "pbs_only" || s === "ls_only" ? "blue" : "gray";

function ActiveBadge({ v }: { v: boolean | null }) {
  if (v === null) return <span className="text-slate-300">—</span>;
  return v ? <Badge color="green">Active</Badge> : <Badge color="gray">Inactive</Badge>;
}

export default function ComparisonView({ system }: { system: "exact" | "ls" }) {
  const left = system === "exact" ? "PBS" : "LS";
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [leftStatus, setLeftStatus] = useState<"all" | "active" | "inactive">("all");
  const [page, setPage] = useState(1);
  const utils = trpc.useUtils();

  const q = trpc.comparison.view.useQuery(
    { system, search: search || undefined, status, leftStatus, page, pageSize: 50 },
    { staleTime: 60_000 },
  );
  const s = q.data?.stats ?? {};
  const totalPages = q.data ? Math.max(1, Math.ceil(q.data.total / 50)) : 1;
  const setFilter = (f: StatusFilter) => { setStatus(f); setPage(1); };

  async function exportData(kind: "csv" | "xls") {
    const rows = await utils.comparison.exportRows.fetch({ system, search: search || undefined, status, leftStatus });
    const headers = ["Code", `${left} Description`, `${left} Active`, system === "ls" ? "LS Visibility" : "Brand", "ExactOnline ItemCode", "ExactOnline Description", "ExactOnline Active", "Status"];
    const data = rows.map((r) => [r.code, r.leftDescription ?? "", r.leftActive == null ? "" : r.leftActive ? "Active" : "Inactive", (system === "ls" ? r.leftExtra : r.leftBrand) ?? "", r.exactItemCode ?? "", r.exactDescription ?? "", r.exactActive == null ? "" : r.exactActive ? "Active" : "Inactive", STATUS_LABEL[r.status] ?? r.status]);
    const today = todayIso(); // Amsterdam date — toISOString() is UTC and names the file a day early after midnight
    if (kind === "csv") {
      const esc = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
      const csv = "﻿" + [headers, ...data].map((row) => row.map(esc).join(",")).join("\n");
      downloadBlob(csv, `${system}-comparison-${today}.csv`, "text/csv");
    } else {
      const cell = (v: string) => `<td>${String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;")}</td>`;
      const html = `<table><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr>${data.map((r) => `<tr>${r.map(cell).join("")}</tr>`).join("")}</table>`;
      downloadBlob(html, `${system}-comparison-${today}.xls`, "application/vnd.ms-excel");
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">{system === "exact" ? "PBS vs ExactOnline" : "Lightspeed vs ExactOnline"}</h1>
        <p className="flex items-center gap-2 text-sm text-slate-500">
          Compare product status between {system === "exact" ? "the PBS catalog" : "Lightspeed eCom"} and ExactOnline
          {q.data && <span>· Fetched {fmtDateTime(q.data.fetchedAt)}</span>}
          {system === "exact" && (q.data?.excludedInactive ?? 0) > 0 && <Badge color="red">{fmtNum(q.data!.excludedInactive)} excluded (ExactOnline non-active)</Badge>}
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <Stat accent="indigo" label={`${left} products`} value={fmtNum(system === "exact" ? s.pbsProducts : s.lsProducts)} />
        <Stat label={system === "exact" ? "ExactOnline supplier items" : "ExactOnline items"} value={fmtNum(system === "exact" ? s.exactSupplierItems : s.exactItems)} />
        <button onClick={() => setFilter("all")} className="text-left"><Stat accent="green" label="Matched" value={fmtNum(s.matched)} /></button>
        <button onClick={() => setFilter("left_only")} className="text-left"><Stat label={`${left} only`} value={fmtNum(system === "exact" ? s.pbsOnly : s.lsOnly)} /></button>
        <button onClick={() => setFilter("exact_only")} className="text-left"><Stat label="ExactOnline only" value={fmtNum(s.exactOnly)} /></button>
      </div>

      {/* Mismatch panel */}
      <Card className="border-amber-200 bg-amber-50/40">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><span>⚠</span> Status Mismatches</div>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <button onClick={() => setFilter("both_active")} className="flex items-center gap-2 text-left text-sm"><span className="text-green-600">✓</span> Both Active: <b>{fmtNum(s.bothActive)}</b></button>
          <button onClick={() => setFilter("left_active_exact_inactive")} className="flex items-center gap-2 text-left text-sm"><span className="text-amber-600">⚠</span> {left} Active / ExactOnline Inactive: <b className="text-amber-700">{fmtNum(system === "exact" ? s.mismatchPbsActive : s.mismatchLsActive)}</b></button>
          <button onClick={() => setFilter("left_inactive_exact_active")} className="flex items-center gap-2 text-left text-sm"><span className="text-amber-600">⚠</span> {left} Inactive / ExactOnline Active: <b className="text-amber-700">{fmtNum(s.mismatchExactActive)}</b></button>
        </div>
      </Card>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search by code, description, brand…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="w-80" />
        <Select value={leftStatus} onChange={(e) => { setLeftStatus(e.target.value as typeof leftStatus); setPage(1); }}>
          <option value="all">All {left}</option><option value="active">{left} Active</option><option value="inactive">{left} Inactive</option>
        </Select>
        <Select value={status} onChange={(e) => setFilter(e.target.value as StatusFilter)}>
          <option value="all">All statuses</option>
          <option value="mismatches">All Mismatches</option>
          <option value="both_active">Both Active</option>
          <option value="both_inactive">Both Inactive</option>
          <option value="left_active_exact_inactive">{left} Active / ExactOnline Inactive</option>
          <option value="left_inactive_exact_active">{left} Inactive / ExactOnline Active</option>
          <option value="left_only">Only in {left}</option>
          <option value="exact_only">Only in ExactOnline</option>
        </Select>
        <Button variant="secondary" onClick={() => exportData("csv")}>Export CSV</Button>
        <Button variant="secondary" onClick={() => exportData("xls")}>Export Excel</Button>
        <Button variant="secondary" onClick={() => q.refetch()}>↻ Refresh</Button>
      </div>

      <div className="text-sm text-slate-500">{fmtNum(q.data?.total)} items{status !== "all" || leftStatus !== "all" || search ? " (filtered)" : ""}</div>

      <Card pad={false}>
        {q.isLoading ? (
          <div className="p-5"><Spinner label="Comparing…" /></div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="p-3">Code</th><th>{left} Description</th><th>{left}</th>
                {system === "ls" && <th>LS Visibility</th>}
                <th>ExactOnline ItemCode</th><th>ExactOnline Description</th><th>ExactOnline</th><th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.data?.rows.map((r) => (
                <tr key={r.code} className="hover:bg-slate-50">
                  <td className="p-3 font-mono text-xs">{r.code}</td>
                  <td className="max-w-xs truncate">{r.leftDescription}</td>
                  <td><ActiveBadge v={r.leftActive} /></td>
                  {system === "ls" && <td className="text-xs text-slate-500">{r.leftExtra}</td>}
                  <td className="font-mono text-xs">{r.exactItemCode}</td>
                  <td className="max-w-xs truncate">{r.exactDescription}</td>
                  <td><ActiveBadge v={r.exactActive} /></td>
                  <td className="p-3"><Badge color={statusColor(r.status) as "green" | "amber" | "blue" | "gray"}>{STATUS_LABEL[r.status] ?? r.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="flex items-center justify-between">
        <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Previous</Button>
        <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
        <Button variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next →</Button>
      </div>
    </div>
  );
}

function downloadBlob(content: string, filename: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
